<template>
  <div
    class="font-sans"
    :class="{ 'single-line': singleLine }"
  >
    <template v-if="singleLine">
      <div
        v-if="latestComment"
        class="flex items-center justify-between px-2 h-full box-border"
        :class="{ 'border-r-4 border-danger pr-1.5': latestComment.hasError }"
      >
        <span class="flex-grow whitespace-nowrap overflow-hidden text-ellipsis">{{ truncatedLatestComment }}</span>
        <span
          v-if="activeCommentCount > 1"
          class="inline-flex items-center justify-center min-w-[20px] h-5 ml-2 rounded-full bg-info text-fg-on-accent text-xs"
        >
          {{ activeCommentCount }}
        </span>
      </div>
    </template>
    <template v-else>
      <div
        v-for="(comment, index) in sortedComments"
        :key="comment.commentId"
        class="border border-border rounded p-2.5 mb-2.5 relative"
        :class="{ 'border-r-4 border-r-danger pr-1.5': comment.hasError }"
      >
        <div class="flex justify-between items-center mb-2">
          <div class="flex items-center gap-2">
            <!-- Bug 3538: an imported comment names the person in ONE place. The channel lives in
                 userName ("Import") and the person in importedBy, so the header reads Importert
                 <date> (av <name>) instead of repeating the name on both sides of the date. -->
            <span
              v-if="comment.isImported"
              class="font-bold"
            >Importert</span>
            <span
              v-else
              class="font-bold"
              :class="{ 'underline decoration-danger': comment.hasError && isSystemUser(comment.userName) }"
            >{{ comment.userName }}</span>
            <span
              class="text-fg-muted text-[0.9em]"
              :class="{ 'underline decoration-danger': comment.hasError && isSystemUser(comment.userName) }"
            >
              {{ formatDate(comment.timeStamp) }}
            </span>
            <span
              v-if="comment.isEdited"
              class="text-fg-muted text-[0.9em]"
            >
              (Redigert: {{ comment.editedBy }} - {{ formatDate(comment.lastEdit) }})
            </span>
            <span
              v-if="comment.isImported && comment.importedBy"
              class="text-fg-muted text-[0.85em] italic"
            >
              (av {{ comment.importedBy }})
            </span>
          </div>
          <div
            v-if="!readOnly"
            class="flex gap-3"
          >
            <BaseIconButton
              icon="pencil"
              type="primary"
              size="small"
              title="Rediger"
              aria-label="Rediger"
              @click="startEditing(index)"
            />
            <BaseIconButton
              icon="trash-can"
              type="delete"
              size="small"
              title="Slett"
              aria-label="Slett"
              @click="showDeleteConfirm(index)"
            />
          </div>
        </div>
        <div
          v-if="editingIndex === index"
          class="mt-2.5"
        >
          <AutoExpandingTextarea
            v-model="editedComment"
            placeholder="Rediger kommentar..."
            :submit-icon="commentStore.isUpdating ? 'spinner' : 'check'"
            :submit-text="commentStore.isUpdating ? 'Oppdaterer...' : 'Oppdater'"
            :disabled="commentStore.isUpdating"
            action-buttons
            @submit="saveEdit(index)"
            @cancel="cancelEdit"
          />
        </div>
        <div
          v-else
          class="whitespace-pre-wrap break-words leading-relaxed"
          :class="{ 'hidden': comment.isDeleted }"
        >
          {{ comment.comment }}
        </div>
        <div
          v-if="comment.isDeleted"
          class="hidden"
        >
          {{ formatDeletedInfo(comment) }}
        </div>
      </div>

      <div
        v-if="!readOnly"
        class="mt-5 relative"
      >
        <AutoExpandingTextarea
          v-model="newComment"
          placeholder="Skriv en ny kommentar..."
          :submit-icon="commentStore.isCreating ? 'spinner' : 'paper-plane'"
          :submit-text="commentStore.isCreating ? 'Lagrer...' : 'Legg til'"
          :disabled="commentStore.isCreating"
          action-buttons
          @submit="addComment"
          @cancel="cancelNewComment"
        />
      </div>
    </template>

    <BaseDialog
      :model-value="deleteDialogVisible"
      title="Bekreft sletting"
      @close="cancelDelete"
    >
      <p>Er du sikker på at du vil slette denne kommentaren?</p>

      <template #footer>
        <BaseIconButton
          icon="times"
          @click="cancelDelete"
        >
          Nei, avbryt
        </BaseIconButton>
        <BaseIconButton
          icon="check"
          type="delete"
          :disabled="commentStore.isDeleting"
          @click="confirmDelete"
        >
          {{ commentStore.isDeleting ? 'Sletter...' : 'Ja, slett' }}
        </BaseIconButton>
      </template>
    </BaseDialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useCommentStore } from '@/store/commentStore';
import { useTableStore } from '@/store/storeTable';
import { useUserPrefFormatting } from '@/composables/useUserPrefFormatting';
import AutoExpandingTextarea from '@/components/BaseComponents/AutoExpandingTextarea.vue';
import BaseIconButton from '@/components/BaseComponents/BaseIconButton.vue';
import BaseDialog from '@/components/BaseComponents/BaseDialog.vue';

const commentStore = useCommentStore();
const tableStore = useTableStore();
const { formatDateTime } = useUserPrefFormatting();
const deleteDialogVisible = ref(false);
const deleteIndex = ref(-1);

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ comments: [] })
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  // US 3399: id deklareres, men videreformidles BEVISST ikke - denne komponenten rendrer ingen
  // labelable kontroll aa peke paa. Deklarasjonen finnes for aa STOPPE Vues attribute-fallthrough:
  // uten den ville en id havnet paa rot-diven, og en <label for=> dit ville tilfredsstilt statisk
  // analyse mens HTMLLabelElement.control fortsatt var null. Se editComponentAcceptsId.
  id: { type: String, default: undefined }
});

const emit = defineEmits(['update:modelValue']);

const commentThread = computed(() => {
  if (props.modelValue && Array.isArray(props.modelValue.comments)) {
    return props.modelValue;
  }
  return { comments: [] };
});

const sortedComments = computed(() => {
  return [...commentThread.value.comments]
    .filter(comment => !comment.isDeleted)
    .sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp));
});

const latestComment = computed(() => {
  return sortedComments.value[sortedComments.value.length - 1];
});

const truncatedLatestComment = computed(() => {
  if (!latestComment.value) return '';
  return latestComment.value.comment.length > 50
    ? latestComment.value.comment.substring(0, 50) + '...'
    : latestComment.value.comment;
});

const editingIndex = ref(-1);
const editedComment = ref('');
const newComment = ref('');

// WI 2086: tidsstemplene følger brukerens datoformat-/tidssone-prefs.
// formatDateTime gir '' for null/undefined/tom verdi.
function formatDate (date) {
  return formatDateTime(date);
}

function formatDeletedInfo (comment) {
  const deletedByPart = comment?.deletedBy ? ` av ${comment.deletedBy}` : '';
  const deletedTimeText = formatDate(comment?.deletedTime);
  return deletedTimeText ? `Slettet${deletedByPart} ${deletedTimeText}` : `Slettet${deletedByPart}`;
}

function startEditing (index) {
  editingIndex.value = index;
  editedComment.value = commentThread.value.comments[index].comment;
}

async function saveEdit (index) {
  if (editedComment.value.trim() && !commentStore.isUpdating) {
    const updatedComments = [...commentThread.value.comments];
    const commentData = {
      ...updatedComments[index],
      comment: editedComment.value.trim(),
      isEdited: true,
      lastEdit: new Date()
    };

    if (props.autoSave) {
      const tableKey = getTableKey();
      if (!tableKey) {
        return;
      }
      const payload = {
        TableKey: tableKey,
        RowId: props.rowId,
        ColumnName: props.header.field,
        CommentId: commentData.commentId,
        Comment: commentData.comment
      };

      const response = await commentStore.updateComment(payload);
      if (!response) {
        commentData.hasError = true;
        return;
      }
    }

    updatedComments[index] = commentData;
    emit('update:modelValue', { ...commentThread.value, comments: updatedComments });
    editingIndex.value = -1;
    editedComment.value = '';
  }
}

function cancelEdit () {
  editingIndex.value = -1;
  editedComment.value = '';
}

function isSystemUser (userName) {
  return typeof userName === 'string' && userName.startsWith('System');
}

function getTableKey () {
  const fromHeader = String(props.header?.tableKey || '').trim();
  if (fromHeader) return fromHeader;
  const fromStore = String(tableStore.tableKey || '').trim();
  if (fromStore) return fromStore;
  return null;
}

async function addComment () {
  if (newComment.value.trim() && !commentStore.isCreating) {
    if (props.autoSave) {
      const tableKey = getTableKey();
      if (!tableKey) {
        return;
      }
      const payload = {
        TableKey: tableKey,
        RowId: props.rowId,
        ColumnName: props.header.field,
        Comment: newComment.value.trim()
      };

      const response = await commentStore.createComment(payload);
      if (response && response.data) {
        emit('update:modelValue', response.data);
        newComment.value = '';
      }
    } else {
      const updatedComments = [...commentThread.value.comments, {
        commentId: crypto.randomUUID(),
        comment: newComment.value.trim(),
        userName: '(lokal)',
        timeStamp: new Date().toISOString(),
        isDeleted: false
      }];
      emit('update:modelValue', { ...commentThread.value, comments: updatedComments });
      newComment.value = '';
    }
  }
}

function cancelNewComment () {
  newComment.value = '';
}

function showDeleteConfirm (index) {
  deleteIndex.value = index;
  deleteDialogVisible.value = true;
}

async function confirmDelete () {
  if (deleteIndex.value !== -1 && !commentStore.isDeleting) {
    const commentToDelete = commentThread.value.comments[deleteIndex.value];
    const tableKey = getTableKey();
    if (!tableKey) {
      return;
    }
    const payload = {
      TableKey: tableKey,
      RowId: props.rowId,
      ColumnName: props.header.field,
      CommentId: commentToDelete.commentId
    };

    const response = await commentStore.deleteComment(payload);
    if (response?.data) {
      emit('update:modelValue', response.data);
      cancelDelete(); // ✅ Lukk KUN ved success
    }
    // Ved feil: IKKE lukk, la bruker prøve igjen
  }
}

function cancelDelete () {
  deleteDialogVisible.value = false;
  deleteIndex.value = -1;
}

const activeCommentCount = computed(() => {
  return commentThread.value.comments.filter(comment => !comment.isDeleted).length;
});
</script>

